import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, ConfigProvider, notification, Select, DatePicker, Checkbox, Card, Row, Col, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { CloseCircleOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import changeHeight from '../data/changeHeight';
import axios from 'axios';
import dayjs from 'dayjs';
import esES from 'antd/es/locale/es_ES';
import locale from 'antd/es/date-picker/locale/es_ES';

dayjs.locale('es');
const { Option } = Select;
const { Title, Text } = Typography;

const VitalSeguro = () => {
    const [buttonLoading, setButtonLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [primaryColor, setPrimaryColor] = useState('#FFFFFF');
    const [urlParent, setUrlParent] = useState(null);
    const [searchParams] = useSearchParams();
    const [form] = Form.useForm();
    const refForm = useRef(0);
    const [api, contextHolder] = notification.useNotification();
    const [numAsegurados, setNumAsegurados] = useState(1);
    const [precios, setPrecios] = useState(null);

    useEffect(() => {
        if (searchParams.get("primaryColor")) {
            setPrimaryColor('#' + searchParams.get("primaryColor"));
        }
        if (searchParams.get("parentUrl")) {
            setUrlParent(searchParams.get("parentUrl"));
            form.setFieldsValue({ url: decodeURIComponent(searchParams.get("parentUrl")) });
        }
    }, [searchParams, form]);

    useEffect(() => {
        const element = refForm.current;
        if (!element) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.target === element) {
                    changeHeight(entry.target.clientHeight);
                    break;
                }
            }
        });
        resizeObserver.observe(element);
        return () => {
            resizeObserver.unobserve(element);
        };
    }, []);

    window.addEventListener('message', (event) => {
        if (event.data.type === "from-parent") {
            if (event.data.fbp === 'null' || event.data.fbp === null || event.data.fbp === undefined) {
                form.setFieldsValue({ fbp: " " });
            } else {
                form.setFieldsValue({ fbp: event.data.fbp });
            }
            if (event.data.hubspotutk === 'null' || event.data.hubspotutk === null || event.data.hubspotutk === undefined) {
                form.setFieldsValue({ hubspotutk: " " });
            } else {
                form.setFieldsValue({ hubspotutk: event.data.hubspotutk });
            }
            if (event.data.userAgent) { form.setFieldsValue({ userAgent: event.data.userAgent }); }
            changeHeight(refForm.current.clientHeight);
        }
    });


    const onFinish = async (values) => {
        setButtonLoading(true);
        try {
            const requestData = { provincia: values.provincia, num_asegurados: values.num_asegurados };
            for (let i = 1; i <= values.num_asegurados; i++) {
                const fechaNacimiento = values[`fecha_nacimiento_${i}`];
                if (fechaNacimiento) {
                    requestData[`fecha${i}`] = dayjs(fechaNacimiento).format('DD/MM/YYYY');
                }
            }
            const response = await axios.post('https://n8n-615087458109.europe-southwest1.run.app/webhook/0e48142f-eb3d-4f7e-a869-4ccc8110a162', requestData, {
                headers: { 'Content-Type': 'application/json' }
            });
            let data = response.data;
            if (typeof data === 'string') {
                try { data = JSON.parse(data); } catch (e) { throw new Error('Error al parsear la respuesta del servidor'); }
            }
            if (data && Array.isArray(data) && data.length > 0) {
                setPrecios(data);
                setSuccess(true);
            } else {
                throw new Error('Respuesta inválida del servidor');
            }
        } catch (error) {
            openNotification('bottomRight');
        } finally {
            setButtonLoading(false);
        }
    };

    const openNotification = () => {
        api.info({
            message: 'Error inesperado',
            description: 'Ha ocurrido un error inesperado. Inténtelo más tarde. Disculpe las molestias',
            placement: 'bottomRight',
            icon: <CloseCircleOutlined style={{ color: "#f7151c" }} />
        });
    };

    const handleNumAseguradosChange = (value) => {
        setNumAsegurados(value);
        const fieldsToReset = {};
        for (let i = 1; i <= 9; i++) {
            fieldsToReset[`fecha_nacimiento_${i}`] = undefined;
        }
        form.setFieldsValue(fieldsToReset);
    };

    const disableDateAfterToday = (current) => current && current >= dayjs().endOf('day');

    const renderDatePickers = () => {
        const datePickers = [];
        for (let i = 1; i <= numAsegurados; i++) {
            datePickers.push(
                <Form.Item
                    key={i}
                    label={<p style={{ fontSize: "15px", marginTop: "-6px", marginBottom: "-6px" }}>Fecha de nacimiento asegurado nº {i} *</p>}
                    name={`fecha_nacimiento_${i}`}
                    style={{ marginBottom: "0.6rem" }}
                    rules={[{ required: true, message: `Por favor, ingresa la fecha de nacimiento del asegurado nº ${i}` }]}
                >
                    <DatePicker
                        style={{ width: '100%' }}
                        locale={locale}
                        disabled={buttonLoading}
                        format="DD/MM/YYYY"
                        showToday={false}
                        disabledDate={disableDateAfterToday}
                        defaultPickerValue={dayjs().subtract(25, 'years')}
                        placeholder="dd/mm/yyyy"
                    />
                </Form.Item>
            );
        }
        return datePickers;
    };

    const renderPreciosCards = () => {
        if (!precios || precios.length === 0) return null;
        const planes = [
            { nombre: 'Estable Óptima', precio: precios[0]?.PrimaBrutaConCampanya || precios[0]?.PrimaBrutaSinCampanya, color: '#FF6B35', gradient: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)', features: ['Copago'], masInfo: 'https://www.vitalseguro.com/seguro-medico/' },
            { nombre: 'Estable', precio: precios[1]?.PrimaBrutaConCampanya || precios[1]?.PrimaBrutaSinCampanya, color: '#4F46E5', gradient: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)', features: ['Copago reducido'], recommended: true, masInfo: 'https://www.vitalseguro.com/seguro-medico/' },
            { nombre: 'Estable Plus', precio: precios[2]?.PrimaBrutaConCampanya || precios[2]?.PrimaBrutaSinCampanya, color: '#059669', gradient: 'linear-gradient(135deg, #059669 0%, #10B981 100%)', features: ['Sin copago'], masInfo: 'https://www.vitalseguro.com/seguro-medico/' }
        ];

        return (
            <div style={{ padding: '1.5rem', background: '#f8fafc' }}>
                <style>{`
                    @keyframes slideInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
                    @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
                    .card-hover { transition: all 0.3s ease; }
                    .card-hover:hover { transform: translateY(-3px) scale(1.01); box-shadow: 0 10px 25px rgba(0,0,0,0.12) !important; }
                    @media (max-width: 768px) { .card-hover { margin: 0 0.25rem !important; transform: none !important; } .card-hover:hover { transform: translateY(-2px) scale(1.005) !important; } }
                    @media (max-width: 480px) { .card-hover { margin: 0 !important; border-radius: 12px !important; } }
                `}</style>

                <Row gutter={[16, 16]} justify="center">
                    <Col span={24} style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>
                        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                            <Title level={4} style={{ color: '#6b7280', fontSize: '14px', fontWeight: '600', marginBottom: '1rem' }}>
                                Coberturas incluidas en todos los planes
                            </Title>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.5rem', padding: '0 1rem' }}>
                            {['Asistencia primaria', 'Urgencias 24/7', 'Hospitalización', 'Especialidades médicas', 'Seguimiento del embarazo', 'Medicina preventiva', 'Odontología', 'Diagnóstico y tratamiento', 'Cirugía ambulatoria', '¡Y mucho más!'].map((cobertura, index) => (
                                <div key={index} style={{ display: 'inline-flex', alignItems: 'center', padding: '0.4rem 0.8rem', background: '#f8fafc', borderRadius: '16px', border: '1px solid #e2e8f0', fontSize: '12px', color: '#4b5563', fontWeight: '500', marginBottom: '0.5rem' }}>
                                    <span style={{ marginRight: '0.4rem', color: '#10b981', fontSize: '12px' }}>✓</span>
                                    {cobertura}
                                </div>
                            ))}
                        </div>
                    </Col>
                    {planes.map((plan, index) => (
                        <Col xs={24} sm={12} lg={8} key={index} style={{ marginBottom: '1rem' }}>
                            <Card className="card-hover" style={{ textAlign: 'center', borderRadius: '16px', boxShadow: plan.recommended ? '0 15px 35px rgba(79, 70, 229, 0.25)' : '0 8px 25px rgba(0,0,0,0.08)', border: plan.recommended ? '2px solid #4F46E5' : '1px solid #e5e7eb', height: '100%', display: 'flex', flexDirection: 'column', background: 'white', overflow: 'hidden', position: 'relative', animation: `slideInUp 0.8s ease-out ${index * 0.2}s both`, transform: plan.recommended ? 'scale(1.02)' : 'scale(1)', zIndex: plan.recommended ? 10 : 1, margin: '0 0.5rem' }} bodyStyle={{ padding: '0', height: '100%', display: 'flex', flexDirection: 'column' }}>
                                {plan.recommended && (
                                    <div style={{ position: 'absolute', top: '10px', right: '10px', background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', color: '#1f2937', padding: '6px 12px', borderRadius: '15px', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px', boxShadow: '0 3px 10px rgba(255, 215, 0, 0.3)', zIndex: 20, animation: 'pulse 2s infinite', maxWidth: '120px', textAlign: 'center', lineHeight: '1.2' }}>
                                        RECOMENDADO
                                    </div>
                                )}

                                <div style={{ background: plan.gradient, padding: '1.2rem 1rem 0.8rem', color: 'white', position: 'relative' }}>
                                    <Title level={2} style={{ color: 'white', marginBottom: '0.5rem', fontSize: '22px', fontWeight: 'bold', marginTop: '0', lineHeight: '1.2' }}>
                                        {plan.nombre}
                                    </Title>
                                    <Text style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', fontWeight: '500' }}>
                                        {plan.subtitulo}
                                    </Text>
                                </div>

                                <div style={{ padding: '1.5rem 1rem', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                    <div>
                                        <div style={{ marginBottom: '1.2rem', padding: '0.8rem', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', borderRadius: '12px', border: `2px solid ${plan.color}20` }}>
                                            <div style={{ marginBottom: '0.4rem' }}>
                                                <Text style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    Precio mensual
                                                </Text>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center' }}>
                                                <Text style={{ fontSize: '42px', fontWeight: '900', color: plan.color, lineHeight: 1, textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                                    {Math.floor(plan.precio)}
                                                </Text>
                                                <Text style={{ fontSize: '20px', color: plan.color, marginLeft: '3px', fontWeight: 'bold' }}>
                                                    ,{Math.round((plan.precio % 1) * 100).toString().padStart(2, '0')} €
                                                </Text>
                                            </div>
                                        </div>
                                        <div style={{ marginBottom: '1.2rem' }}>
                                            {plan.features.map((feature, idx) => (
                                                <div key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.4rem', padding: '0.4rem', background: '#f8fafc', borderRadius: '6px' }}>
                                                    <span style={{ marginRight: '0.4rem', color: plan.color, fontSize: '14px' }}>✓</span>
                                                    <Text style={{ fontSize: '12px', color: '#4a5568', fontWeight: '500' }}>{feature}</Text>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div style={{ marginBottom: '1rem' }}>
                                        <Link to={plan.masInfo} target='_blank'>
                                            <Button size="small" style={{ width: '100%', border: '0px', fontSize: '12px' }} type='link'>MÁS INFORMACIÓN</Button>
                                        </Link>
                                    </div>
                                    <Button type="primary" size="large" style={{ width: '100%', height: '42px', borderRadius: '20px', fontSize: '14px', fontWeight: 'bold', letterSpacing: '0.3px', background: plan.gradient, border: 'none', boxShadow: '0 6px 15px rgba(0,0,0,0.12)', textTransform: 'uppercase' }} onMouseEnter={(e) => { e.target.style.transform = 'translateY(-1px)'; e.target.style.boxShadow = '0 8px 20px rgba(0,0,0,0.15)'; }} onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 6px 15px rgba(0,0,0,0.12)'; }}>
                                        CONTRATAR AHORA
                                    </Button>
                                </div>

                            </Card>

                        </Col>
                    ))}

                </Row>

            </div>
        );
    };


    return (
        <>
            {contextHolder}
            <ConfigProvider
                locale={esES}
                theme={{
                    token: {
                        colorPrimary: primaryColor,
                    },
                }}
            >
                <style>{`::-webkit-scrollbar { display: none; }`}</style>

                <div ref={refForm}>
                    {!success ? (
                        <Form form={form} style={{ padding: '1.5rem' }} name="vitalSeguro" initialValues={{ remember: true, url: urlParent }} layout='vertical' onFinish={onFinish} requiredMark={false}>
                            <Form.Item hidden={true} label="URL" name="url"><Input type='text' /></Form.Item>
                            <Form.Item hidden={true} label="FB" name="fbp"><Input type='' /></Form.Item>
                            <Form.Item hidden={true} label="Hubspotutk" name="hubspotutk"><Input type='' /></Form.Item>
                            <Form.Item hidden={true} label="userAgent" name="userAgent"><Input type='' /></Form.Item>

                            <Form.Item label={<p style={{ fontSize: "15px", marginTop: "-6px", marginBottom: "-6px" }}>Provincia de residencia *</p>} name="provincia" style={{ marginBottom: "0.6rem" }} rules={[{ required: true, message: 'Por favor, selecciona tu provincia de residencia' }]}>
                                <Select style={{ width: '100%' }} placeholder='Selecciona tu provincia' disabled={buttonLoading}>
                                    <Option value="Barcelona">Barcelona</Option>
                                    <Option value="Girona">Girona</Option>
                                    <Option value="Lleida">Lleida</Option>
                                    <Option value="Tarragona">Tarragona</Option>
                                </Select>
                            </Form.Item>
                            <Form.Item label={<p style={{ fontSize: "15px", marginTop: "-6px", marginBottom: "-6px" }}>Número de asegurados *</p>} name="num_asegurados" style={{ marginBottom: "0.6rem" }} rules={[{ required: true, message: 'Por favor, selecciona el número de asegurados' }]}>
                                <Select style={{ width: '100%' }} placeholder='Selecciona el número de asegurados' disabled={buttonLoading} onChange={handleNumAseguradosChange}>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (<Option key={num} value={num}>{num}</Option>))}
                                </Select>
                            </Form.Item>

                            {renderDatePickers()}

                            <Form.Item label={<p style={{ fontSize: "15px", marginTop: "-6px", marginBottom: "-6px" }}>¿Dónde te llamamos? *</p>} name="tel" style={{ marginBottom: "0.6rem" }} rules={[{ required: true, message: 'Por favor, ingresa tu teléfono' }]}>
                                <Input maxLength={9} minLength={9} placeholder='619 99 31 31' type='tel' disabled={buttonLoading} />
                            </Form.Item>
                            <Form.Item label={<p style={{ fontSize: "15px", marginTop: "-6px", marginBottom: "-6px" }}>¿Dónde te podemos enviar el email? *</p>} name="email" style={{ marginBottom: "0.6rem" }} rules={[{ required: true, message: 'Por favor, ingresa tu email' }]}>
                                <Input placeholder='tunombre@gmail.com' type='email' disabled={buttonLoading} />
                            </Form.Item>
                            <Form.Item style={{ marginBottom: "0.6rem" }} valuePropName="checked" name="terms" initialValue={false} rules={[{ required: true, validator: async (_, checked) => { if (!checked) { return Promise.reject(new Error("Por favor, acepta los términos y condiciones")); } } }]}>
                                <Checkbox style={{ fontSize: "15px" }} disabled={buttonLoading}>Acepto los <Link target='_blank' to='https://www.coventia.es/politica-de-privacidad-y-aviso-de-cookies'>términos y condiciones</Link></Checkbox>
                            </Form.Item>
                            <Form.Item style={{ marginBottom: "0.6rem" }} valuePropName="checked" name="whatsapp" initialValue={false}>
                                <Checkbox style={{ fontSize: "15px" }} disabled={buttonLoading} >Acepto recibir asesoramiento vía WhatsApp</Checkbox>
                            </Form.Item>
                            <Form.Item style={{ marginBottom: "0.6rem", width: '100%', marginTop: '1rem' }}>
                                <Button type='primary' loading={buttonLoading} htmlType="submit" style={{ width: "100%", height: "35px" }}>
                                    {buttonLoading ? 'Calculando...' : 'Calcular precios'}
                                </Button>
                            </Form.Item>
                        </Form>
                    ) : (
                        renderPreciosCards()
                    )}
                </div>
            </ConfigProvider>
        </>
    );
};

export default VitalSeguro;
